import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { format } from "date-fns";
import { toast } from "react-toastify";
import MessagesGrid from "../notificationCenter/MessagesGrid";
import { CHANNEL_NAMES } from "../notificationCenter/channelText";
import { KpiCard, KpiGrid, SA, SaCard, StatusPill, TONES, Tone, saTableBodySx, saTableHeadSx, saToggleSx } from "./saUi";
import { PriceChangesTable, PriceForm, PriceHistoryTable } from "./PriceEditor";
import {
  CENTER_CHANNELS,
  CenterChannel,
  ChannelInfo,
  NotificationCenterOverview,
  NotificationUsage,
  OrganizationPricing,
  PaidChannel,
  deleteOrganizationPriceChange,
  formatMoney,
  formatUnitPrice,
  getOrganizationNotificationCenter,
  getOrganizationNotificationUsage,
  getOrganizationPricing,
  setOrganizationNotificationChannel,
  setOrganizationPrice,
  usageRange,
} from "../../services/notificationCenterService";

const errorMessage = (err: any, fallback: string) => {
  const message = err?.response?.data?.message;
  return (Array.isArray(message) ? message[0] : message) || fallback;
};

const CHANNEL_LOOK: Record<CenterChannel, { icon: React.ReactNode; tone: Tone }> = {
  PUSH: { icon: <NotificationsActiveOutlinedIcon />, tone: "violet" },
  EMAIL: { icon: <MailOutlineIcon />, tone: "blue" },
  SMS: { icon: <SmsOutlinedIcon />, tone: "amber" },
  WHATSAPP: { icon: <WhatsAppIcon />, tone: "green" },
};

/** This organization's price per channel: set its own, or back to the default. */
const OrganizationPrices: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [pricing, setPricing] = useState<OrganizationPricing | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getOrganizationPricing(organizationId)
      .then(setPricing)
      .catch((err) => setError(errorMessage(err, "Could not load prices.")));
  }, [organizationId]);

  const set = async (channel: PaidChannel, value: { price: number | null; effectiveFrom?: string; note?: string | null }) => {
    try {
      setPricing(await setOrganizationPrice(organizationId, channel, value));
      toast.success(
        value.price === null ? "Back on the default price." : value.effectiveFrom ? "Price change scheduled." : "Price saved.",
        { position: "top-right" }
      );
    } catch (err) {
      toast.error(errorMessage(err, "Could not save the price."), { position: "top-right" });
      throw err;
    }
  };

  return (
    <SaCard
      title="Prices for this organization"
      subtitle="What they pay per delivered message. The owner sees their price and its history, never our cost."
      icon={<SellOutlinedIcon />}
      tone="violet"
    >
      {error && <Alert severity="error">{error}</Alert>}
      {!pricing && !error && <CircularProgress size={22} />}
      {pricing && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {pricing.channels.map((channel) => {
            const current = channel.current;
            const defaultNow = channel.history.find((p) => p.source === "DEFAULT" && new Date(p.from) <= new Date());
            return (
              <Box key={channel.channel}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ color: TONES[CHANNEL_LOOK[channel.channel].tone].fg, display: "flex" }}>
                    {CHANNEL_LOOK[channel.channel].icon}
                  </Box>
                  <Typography sx={{ fontWeight: 650, color: SA.text }}>{CHANNEL_NAMES[channel.channel]}</Typography>
                  <Typography sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: SA.text }}>
                    {current?.price != null ? formatUnitPrice(current.price, pricing.currency) : "No price set"}
                  </Typography>
                  {current?.source === "ORGANIZATION" ? (
                    <StatusPill tone="blue" dot={false}>
                      Own price
                    </StatusPill>
                  ) : current ? (
                    <StatusPill tone="slate" dot={false}>
                      Default price
                    </StatusPill>
                  ) : null}
                  {channel.upcoming && (
                    <StatusPill tone="amber">
                      {channel.upcoming.price === null ? "No price" : formatUnitPrice(channel.upcoming.price, pricing.currency)} from{" "}
                      {format(new Date(channel.upcoming.from), "d MMM yyyy")}
                    </StatusPill>
                  )}
                </Box>
                <PriceForm
                  allowDefault
                  currency={pricing.currency}
                  defaultPrice={defaultNow?.price ?? null}
                  onSubmit={(value) =>
                    set(channel.channel, { price: value.price, effectiveFrom: value.effectiveFrom, note: value.note })
                  }
                />
                <Box sx={{ mt: 1.5 }}>
                  <PriceHistoryTable history={channel.history} currency={pricing.currency} />
                </Box>
              </Box>
            );
          })}
          {pricing.changes.length > 0 && (
            <Box>
              <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", color: SA.text, mb: 1 }}>Own price changes</Typography>
              <PriceChangesTable
                changes={pricing.changes}
                currency={pricing.currency}
                channelNames={CHANNEL_NAMES}
                onDelete={async (change) => {
                  try {
                    setPricing(await deleteOrganizationPriceChange(organizationId, change.id));
                    toast.success("Scheduled change cancelled.", { position: "top-right" });
                  } catch (err) {
                    toast.error(errorMessage(err, "Could not cancel it."), { position: "top-right" });
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </SaCard>
  );
};

/** SMS or WhatsApp for one organization: on/off and its own sender name. */
const PaidChannelCard: React.FC<{
  info: ChannelInfo;
  defaultSender: string;
  onChange: (channel: CenterChannel, change: { enabled?: boolean; senderId?: string | null }) => Promise<void>;
}> = ({ info, defaultSender, onChange }) => {
  const [sender, setSender] = useState(info.senderId ?? "");
  const [busy, setBusy] = useState(false);
  useEffect(() => setSender(info.senderId ?? ""), [info.senderId]);

  const run = async (change: { enabled?: boolean; senderId?: string | null }) => {
    setBusy(true);
    try {
      await onChange(info.channel, change);
    } finally {
      setBusy(false);
    }
  };

  const look = CHANNEL_LOOK[info.channel];
  const status = info.enabled ? (
    <StatusPill tone="green">On</StatusPill>
  ) : info.requestedAt ? (
    <StatusPill tone="amber">Requested {format(new Date(info.requestedAt), "d MMM")}</StatusPill>
  ) : (
    <StatusPill tone="slate">Off</StatusPill>
  );

  return (
    <SaCard
      title={CHANNEL_NAMES[info.channel]}
      subtitle={
        info.enabled && info.enabledAt
          ? `Turned on ${format(new Date(info.enabledAt), "d MMM yyyy")}`
          : "Costs us per message. Turn on once the owner has agreed."
      }
      icon={look.icon}
      tone={look.tone}
      action={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {status}
          <Switch
            checked={info.enabled}
            disabled={busy}
            onChange={(e) => run({ enabled: e.target.checked })}
            slotProps={{ input: { "aria-label": `${CHANNEL_NAMES[info.channel]} on` } }}
          />
        </Box>
      }
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
        <TextField
          size="small"
          label="Sender name"
          placeholder={defaultSender}
          value={sender}
          onChange={(e) => setSender(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 11 } }}
          helperText={
            info.channel === "WHATSAPP"
              ? "For our records. WhatsApp always sends from the Softwarezz number."
              : `Up to 11 letters or numbers, registered with tw1 first. Empty sends as ${defaultSender}.`
          }
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          disableElevation
          disabled={busy || sender.trim() === (info.senderId ?? "")}
          onClick={() => run({ senderId: sender.trim() || null })}
          sx={{ textTransform: "none", minHeight: 40, borderRadius: "10px" }}
        >
          Save
        </Button>
      </Box>
    </SaCard>
  );
};

/** What the period's delivered messages were charged, what they cost us, and the margin. */
export const MoneyKpis: React.FC<{ usage: NotificationUsage }> = ({ usage }) => {
  const cost = usage.cost ?? 0;
  const margin = usage.amount - cost;
  const unpriced = usage.totals.SMS.unpriced + usage.totals.WHATSAPP.unpriced;
  return (
    <Box sx={{ mt: 1.5 }}>
      <KpiGrid min={180}>
        <KpiCard
          label="Charged"
          icon={<ReceiptLongOutlinedIcon />}
          tone="blue"
          value={formatMoney(usage.amount, usage.currency)}
          caption="Delivered SMS and WhatsApp"
        />
        <KpiCard
          label="Our cost"
          icon={<PaidOutlinedIcon />}
          tone="slate"
          value={formatMoney(cost, usage.currency)}
          caption="Including free login codes"
        />
        <KpiCard
          label="Margin"
          icon={<TrendingUpOutlinedIcon />}
          tone={margin < 0 ? "red" : "green"}
          value={formatMoney(margin, usage.currency)}
          caption={usage.amount > 0 ? `${Math.round((margin / usage.amount) * 100)}% of charges` : " "}
        />
        {unpriced > 0 && (
          <KpiCard
            label="Sent without a price"
            icon={<WarningAmberOutlinedIcon />}
            tone="amber"
            highlight
            value={unpriced.toLocaleString()}
            captionTone="amber"
            caption="Delivered before a price was set"
          />
        )}
      </KpiGrid>
    </Box>
  );
};

/**
 * The superadmin side of the notification center: turn SMS and WhatsApp on for
 * an organization, set its sender name, and see what it sent.
 */
const NotificationPanel: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [overview, setOverview] = useState<NotificationCenterOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<"this" | "last">("this");
  const [usage, setUsage] = useState<NotificationUsage | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    getOrganizationNotificationCenter(organizationId)
      .then(setOverview)
      .catch((err) => setError(errorMessage(err, "Could not load notification settings.")));
  }, [organizationId]);

  useEffect(() => {
    setUsageLoading(true);
    getOrganizationNotificationUsage(organizationId, usageRange(period))
      .then(setUsage)
      .catch(() => setUsage(null))
      .finally(() => setUsageLoading(false));
  }, [organizationId, period]);

  const changeChannel = useCallback(
    async (channel: CenterChannel, change: { enabled?: boolean; senderId?: string | null }) => {
      try {
        setOverview(await setOrganizationNotificationChannel(organizationId, channel, change));
        toast.success("Saved.", { position: "top-right" });
      } catch (err) {
        toast.error(errorMessage(err, "Could not save."), { position: "top-right" });
      }
    },
    [organizationId]
  );

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!overview) return <CircularProgress size={24} />;

  const paid = overview.channels.filter((c) => c.paid);
  const perMessage = (usage?.byEvent ?? []).filter((row) =>
    CENTER_CHANNELS.some((c) => row.channels[c].sent || row.channels[c].failed)
  );
  const usedChannels = CENTER_CHANNELS.filter((c) =>
    perMessage.some((row) => row.channels[c].sent || row.channels[c].failed)
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
        {paid.map((info) => (
          <PaidChannelCard
            key={info.channel}
            info={info}
            defaultSender={overview.defaultSenderName}
            onChange={changeChannel}
          />
        ))}
      </Box>

      <OrganizationPrices organizationId={organizationId} />

      <SaCard
        title="Messages sent"
        subtitle={usage?.available === false ? "Counts are not available right now." : "Counted by the notification service."}
        icon={<BarChartOutlinedIcon />}
        action={
          <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && setPeriod(v)} sx={saToggleSx}>
            <ToggleButton value="this">This month</ToggleButton>
            <ToggleButton value="last">Last month</ToggleButton>
          </ToggleButtonGroup>
        }
      >
        <KpiGrid min={180}>
          {CENTER_CHANNELS.map((channel) => {
            const counts = usage?.totals?.[channel];
            return (
              <KpiCard
                key={channel}
                label={CHANNEL_NAMES[channel]}
                icon={CHANNEL_LOOK[channel].icon}
                tone={CHANNEL_LOOK[channel].tone}
                value={usageLoading || !counts ? "…" : counts.sent.toLocaleString()}
                captionTone={counts?.failed ? "red" : undefined}
                caption={
                  usageLoading || !counts
                    ? " "
                    : [
                        counts.failed ? `${counts.failed.toLocaleString()} failed` : "delivered, none failed",
                        channel === "SMS" || channel === "WHATSAPP"
                          ? `${formatMoney(counts.amount, usage?.currency)} charged`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                }
              />
            );
          })}
        </KpiGrid>

        {usage?.available && <MoneyKpis usage={usage} />}

        {perMessage.length > 0 && (
          <Box sx={{ mt: 2.5, border: `1px solid ${SA.border}`, borderRadius: "12px", overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={saTableHeadSx}>
                  <TableCell>Message</TableCell>
                  {usedChannels.map((c) => (
                    <TableCell key={c} align="right">
                      {CHANNEL_NAMES[c]}
                    </TableCell>
                  ))}
                  <TableCell align="right">Charged</TableCell>
                  <TableCell align="right">Our cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={saTableBodySx}>
                {perMessage.map((row) => (
                  <TableRow key={row.event} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ fontWeight: 500, color: SA.text }}>
                      {row.label}
                      {row.free && (
                        <Typography component="span" sx={{ ml: 1 }}>
                          <StatusPill tone="teal" dot={false}>
                            Free to them
                          </StatusPill>
                        </Typography>
                      )}
                    </TableCell>
                    {usedChannels.map((c) => (
                      <TableCell key={c} align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {row.channels[c].sent || row.channels[c].failed ? (
                          <>
                            {row.channels[c].sent}
                            {row.channels[c].failed > 0 && (
                              <Typography component="span" sx={{ ml: 0.75, fontSize: "0.78rem", color: TONES.red.fg }}>
                                {row.channels[c].failed} failed
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography component="span" sx={{ color: SA.textSubtle }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                      {row.free ? "Free" : formatMoney(row.amount, usage?.currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", color: `${SA.textMuted} !important` }}>
                      {formatMoney(row.cost, usage?.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </SaCard>

      <SaCard
        title="Owner's message settings"
        subtitle="Which messages this organization sends, and on which channel. Read-only here."
        icon={<TuneOutlinedIcon />}
        tone="slate"
        action={
          <Button
            size="small"
            onClick={() => setShowSettings((v) => !v)}
            endIcon={
              <ExpandMoreIcon sx={{ transform: showSettings ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
            }
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {showSettings ? "Hide" : "Show"}
          </Button>
        }
      >
        {showSettings ? (
          <Collapse in appear>
            <MessagesGrid events={overview.events} />
          </Collapse>
        ) : undefined}
      </SaCard>
    </Box>
  );
};

export default NotificationPanel;
